import type {
  AdminAppWebhookDelivery,
  AdminAppWebhookDeliveryListQuery,
  AdminAppWebhookDeliveryListResult,
  AdminAppWebhookEndpoint,
  AdminAppWebhookUpsertInput,
  AppWebhookEventKey,
} from "@yahshi/contracts";
import { APP_WEBHOOK_EVENT_KEYS, adminAppWebhookDeliveryListQuerySchema, adminAppWebhookUpsertSchema } from "@yahshi/contracts";
import { M1ApiError } from "./M1AppApi";
import type { AppWebhookApi } from "./AppWebhookApi";

const now = () => new Date().toISOString();

export type DevelopmentWebhookEnqueueInput = {
  appId: string;
  eventKey: AppWebhookEventKey;
  occurrenceKey: string;
  payload: Record<string, unknown>;
};

export type DevelopmentAppWebhookApi = AppWebhookApi & {
  enqueue(input: DevelopmentWebhookEnqueueInput): AdminAppWebhookDelivery | null;
};

type StoredEndpoint = AdminAppWebhookEndpoint & { secret: string };

function emptyEndpoint(appId: string): AdminAppWebhookEndpoint {
  return {
    appId,
    url: "",
    enabled: false,
    events: [...APP_WEBHOOK_EVENT_KEYS],
    secretConfigured: false,
    version: 0,
    updatedAt: null,
  };
}

function publicEndpoint(row: StoredEndpoint): AdminAppWebhookEndpoint {
  const { secret: _, ...rest } = row;
  return { ...rest, secretConfigured: Boolean(row.secret), revealedSecret: undefined };
}

function opaqueSecret(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return `whs_${Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")}`;
}

function keepSecret(value: string | undefined): boolean {
  return value === undefined || value === "" || value.includes("***");
}

export function createDevelopmentAppWebhookApi(): DevelopmentAppWebhookApi {
  const endpoints = new Map<string, StoredEndpoint>();
  const deliveries: AdminAppWebhookDelivery[] = [];

  const deliver = (row: StoredEndpoint, input: DevelopmentWebhookEnqueueInput, at = now()): AdminAppWebhookDelivery | null => {
    if (!row.enabled || !row.url || !row.secret) return null;
    if (!row.events.includes(input.eventKey)) return null;
    const existing = deliveries.find((item) => item.appId === input.appId && item.eventKey === input.eventKey && item.occurrenceKey === input.occurrenceKey);
    if (existing) return existing;
    const delivery: AdminAppWebhookDelivery = {
      id: `awd_${crypto.randomUUID().replaceAll("-", "").slice(0, 24)}`,
      appId: input.appId,
      eventKey: input.eventKey,
      occurrenceKey: input.occurrenceKey,
      status: "delivered",
      attemptCount: 1,
      httpStatus: 204,
      lastError: null,
      payload: input.payload,
      nextAttemptAt: at,
      deliveredAt: at,
      createdAt: at,
      updatedAt: at,
    };
    deliveries.unshift(delivery);
    return delivery;
  };

  const api: DevelopmentAppWebhookApi = {
    async getEndpoint(appId) {
      const row = endpoints.get(appId);
      return row ? publicEndpoint(row) : emptyEndpoint(appId);
    },
    async upsertEndpoint(appId, input: AdminAppWebhookUpsertInput) {
      const parsed = adminAppWebhookUpsertSchema.safeParse(input);
      if (!parsed.success) throw new M1ApiError("VALIDATION_ERROR", "请检查回调配置");
      const current = endpoints.get(appId);
      const expected = parsed.data.expectedVersion;
      const version = current?.version ?? 0;
      if (version !== expected) throw new M1ApiError("WEBHOOK_STALE", "回调配置已被更新，请刷新后重试");
      let secret = current?.secret ?? "";
      let revealedSecret: string | undefined;
      if (keepSecret(parsed.data.secret)) {
        if (!secret) {
          revealedSecret = opaqueSecret();
          secret = revealedSecret;
        }
      } else {
        if ((parsed.data.secret ?? "").length < 16) throw new M1ApiError("VALIDATION_ERROR", "签名密钥至少 16 位");
        secret = parsed.data.secret!;
      }
      const saved: StoredEndpoint = {
        appId,
        url: parsed.data.url,
        enabled: parsed.data.enabled,
        events: parsed.data.events,
        secretConfigured: true,
        version: version + 1,
        updatedAt: now(),
        secret,
      };
      endpoints.set(appId, saved);
      return revealedSecret ? { ...publicEndpoint(saved), revealedSecret } : publicEndpoint(saved);
    },
    async listDeliveries(appId, query: Partial<AdminAppWebhookDeliveryListQuery> = {}): Promise<AdminAppWebhookDeliveryListResult> {
      const parsed = adminAppWebhookDeliveryListQuerySchema.parse(query);
      const matched = deliveries.filter((item) => item.appId === appId);
      const offset = (parsed.page - 1) * parsed.pageSize;
      return { items: matched.slice(offset, offset + parsed.pageSize).map((item) => ({ ...item })), total: matched.length };
    },
    async sendTest(appId) {
      const row = endpoints.get(appId);
      if (!row?.enabled || !row.url || !row.secret) throw new M1ApiError("WEBHOOK_NOT_READY", "请先保存 https 回调地址、签名密钥并启用");
      const eventKey = row.events[0] ?? "order.paid";
      const at = now();
      const occurrenceKey = `test:${crypto.randomUUID()}`;
      const delivery = deliver(row, {
        appId,
        eventKey,
        occurrenceKey,
        payload: { event: eventKey, appId, occurredAt: at, data: { test: true, eventKey } },
      }, at);
      if (!delivery) throw new M1ApiError("WEBHOOK_DELIVERY_NOT_FOUND", "测试事件未入队");
      return { ...delivery };
    },
    async retryDelivery(appId, deliveryId) {
      const current = deliveries.find((item) => item.appId === appId && item.id === deliveryId);
      if (!current) throw new M1ApiError("WEBHOOK_DELIVERY_NOT_FOUND", "投递记录不存在");
      if (current.status === "delivered") return { ...current };
      const at = now();
      current.status = "delivered";
      current.attemptCount += 1;
      current.httpStatus = 204;
      current.lastError = null;
      current.deliveredAt = current.deliveredAt ?? at;
      current.updatedAt = at;
      return { ...current };
    },
    enqueue(input) {
      const row = endpoints.get(input.appId);
      if (!row) return null;
      return deliver(row, input);
    },
  };
  return api;
}
